import { useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp, faRotate } from "@fortawesome/free-solid-svg-icons";
import { CircularProgress } from "@mui/material";
import { setCompanyLogoUrl, uploadCompanyLogo } from "../services/api.service";
import "./sync-company-logo.modal.scss";

/**
 * Forced modal shown when the admin's HR-derived logo is out of sync with the
 * Firestore copy that the public interview funnel reads. There is no "close"
 * button; the admin must either copy the existing HR logo URL into Firestore
 * or re-upload a new image.
 */
interface Props {
    isOpen: boolean;
    companyId: string;
    /** URL coming from MySA / HR9 profile (admin header source) */
    mysaLogoUrl: string;
    /** Logo URL currently saved in Firestore interview_company doc */
    firestoreLogoUrl: string;
    companyName: string;
    /** Called with the URL that ended up persisted to Firestore */
    onSynced: (newLogoUrl: string) => void;
}

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
const MAX_BYTES = 5 * 1024 * 1024;

const SyncCompanyLogoModal = ({
    isOpen,
    companyId,
    mysaLogoUrl,
    firestoreLogoUrl,
    companyName,
    onSynced,
}: Props) => {
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string>("");
    const [isWorking, setIsWorking] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const filePreviewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);

    const onPickFile = () => fileInputRef.current?.click();

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setError("");
        if (!f) {
            setFile(null);
            return;
        }
        if (!ALLOWED_TYPES.includes(f.type)) {
            setError("รองรับเฉพาะไฟล์ PNG, JPG, WEBP, SVG");
            setFile(null);
            return;
        }
        if (f.size > MAX_BYTES) {
            setError("ไฟล์ต้องมีขนาดไม่เกิน 5MB");
            setFile(null);
            return;
        }
        setFile(f);
    };

    const onUseHrLogo = async () => {
        const url = String(mysaLogoUrl || "").trim();
        if (!url) {
            setError("ไม่มีโลโก้จากระบบ HR ให้ใช้ — กรุณาอัปโหลดไฟล์ใหม่");
            return;
        }
        try {
            setIsWorking(true);
            setError("");
            const res = await setCompanyLogoUrl(companyId, url);
            if (res?.data?.is_success) {
                onSynced(res.data.data?.company_logo || url);
            } else {
                setError(res?.data?.error || "ไม่สามารถซิงค์โลโก้ได้");
            }
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.message || "ไม่สามารถซิงค์โลโก้ได้");
        } finally {
            setIsWorking(false);
        }
    };

    const onUploadFile = async () => {
        if (!file) {
            setError("กรุณาเลือกไฟล์โลโก้");
            return;
        }
        try {
            setIsWorking(true);
            setError("");
            const res = await uploadCompanyLogo(companyId, file);
            if (res?.data?.is_success) {
                onSynced(res.data.data?.company_logo || "");
            } else {
                setError(res?.data?.error || "อัปโหลดโลโก้ไม่สำเร็จ");
            }
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.message || "อัปโหลดโลโก้ไม่สำเร็จ");
        } finally {
            setIsWorking(false);
        }
    };

    return (
        <Modal
            show={isOpen}
            centered
            backdrop="static"
            keyboard={false}
            className="sync-logo-modal-control"
        >
            <Modal.Body className="sync-logo-modal-body">
                <h2 className="sync-logo-title">โลโก้บริษัทไม่ตรงกับหน้าผู้สมัคร</h2>
                <p className="sync-logo-description">
                    ระบบตรวจพบว่าโลโก้ <strong>{companyName || "บริษัท"}</strong>{" "}
                    ที่แสดงในหน้า admin ยังไม่ตรงกับโลโก้ที่ผู้สมัคร (หน้าบ้าน) เห็น
                    กรุณายืนยันโลโก้ที่ต้องการใช้โดยเลือกหนึ่งในวิธีด้านล่าง
                </p>

                <div className="sync-logo-compare">
                    <div className="sync-logo-compare-item">
                        <span className="sync-logo-compare-label">โลโก้ใน Admin (ระบบ HR)</span>
                        <div className="sync-logo-compare-preview">
                            {mysaLogoUrl
                                ? <img src={mysaLogoUrl} alt="HR logo" />
                                : <span className="sync-logo-compare-empty">ไม่มีโลโก้</span>}
                        </div>
                    </div>
                    <div className="sync-logo-compare-divider">≠</div>
                    <div className="sync-logo-compare-item">
                        <span className="sync-logo-compare-label">โลโก้บนหน้าผู้สมัคร</span>
                        <div className="sync-logo-compare-preview">
                            {firestoreLogoUrl
                                ? <img src={firestoreLogoUrl} alt="Public logo" />
                                : <span className="sync-logo-compare-empty">ไม่มีโลโก้</span>}
                        </div>
                    </div>
                </div>

                <div className="sync-logo-actions">
                    <Button
                        className="sync-logo-action sync-logo-action-secondary"
                        onClick={onUseHrLogo}
                        disabled={isWorking || !mysaLogoUrl}
                    >
                        <FontAwesomeIcon icon={faRotate} />
                        <span>ใช้โลโก้จากระบบ HR</span>
                    </Button>

                    <div className="sync-logo-divider-or"><span>หรือ</span></div>

                    <div className="sync-logo-upload-zone">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                            style={{ display: "none" }}
                            onChange={onFileChange}
                        />
                        {file ? (
                            <div className="sync-logo-upload-preview">
                                {filePreviewUrl
                                    ? <img src={filePreviewUrl} alt="Selected logo" />
                                    : null}
                                <div className="sync-logo-upload-meta">
                                    <span className="sync-logo-upload-filename">{file.name}</span>
                                    <span
                                        className="sync-logo-upload-change"
                                        onClick={onPickFile}
                                    >
                                        เปลี่ยนไฟล์
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="sync-logo-upload-empty" onClick={onPickFile}>
                                <FontAwesomeIcon icon={faCloudArrowUp} />
                                <span className="sync-logo-upload-main">คลิกเพื่ออัปโหลดโลโก้ใหม่</span>
                                <span className="sync-logo-upload-sub">PNG, JPG, WEBP, SVG · ไม่เกิน 5MB</span>
                            </div>
                        )}
                        <Button
                            className="sync-logo-action sync-logo-action-primary"
                            onClick={onUploadFile}
                            disabled={isWorking || !file}
                        >
                            <FontAwesomeIcon icon={faCloudArrowUp} />
                            <span>อัปโหลดและบันทึก</span>
                        </Button>
                    </div>
                </div>

                {error && <p className="sync-logo-error">{error}</p>}

                {isWorking && (
                    <div className="sync-logo-loading">
                        <CircularProgress size={22} />
                        <span>กำลังบันทึก…</span>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default SyncCompanyLogoModal;
